import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { updateInquiryBatch } from "./inquiryBatchApi";
import { BATCH_STATUSES, PERIOD_STATUSES } from "./inquiryBatchUtils";

const inputClass =
  "h-9 w-full rounded border border-slate-200 bg-white px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function EditInquiryBatchModal({ open, batch, onClose, onSaved }) {
  const [batchName, setBatchName] = useState("");
  const [batchStatus, setBatchStatus] = useState("draft");
  const [periodStatus, setPeriodStatus] = useState("open");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !batch) return;
    setBatchName(batch.batch_name || batch.subject || "");
    setBatchStatus(batch.status || "draft");
    setPeriodStatus(batch.period_status || "open");
    setError("");
    setSubmitting(false);
  }, [open, batch]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, submitting, onClose]);

  if (!open || !batch) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const name = batchName.trim();
    if (!name) {
      setError("Enter a batch name.");
      return;
    }

    setError("");
    setSubmitting(true);
    try {
      const updated = await updateInquiryBatch(batch.id, {
        batch_name: name,
        status: batchStatus,
        period_status: periodStatus,
      });
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to update inquiry batch.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        onClick={() => !submitting && onClose?.()}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-inquiry-batch-title"
        onClick={() => !submitting && onClose?.()}
      >
        <div
          className="tailwind-container relative z-10 w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div className="min-w-0">
              <h2
                id="edit-inquiry-batch-title"
                className="text-[15px] font-semibold text-slate-900"
              >
                Edit inquiry batch
              </h2>
              <p className="mt-0.5 text-[12px] text-slate-500">
                {batch.batch_no || `Batch #${batch.id}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
              style={{ boxShadow: "none" }}
            >
              <FaTimes size={12} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 px-4 py-3">
            <div>
              <label
                htmlFor="edit-inquiry-batch-name"
                className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
              >
                Batch name <span className="text-rose-500">*</span>
              </label>
              <input
                id="edit-inquiry-batch-name"
                type="text"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                className={inputClass}
                disabled={submitting}
                required
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="edit-batch-status"
                  className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                >
                  Batch status
                </label>
                <select
                  id="edit-batch-status"
                  value={batchStatus}
                  onChange={(e) => setBatchStatus(e.target.value)}
                  className={inputClass}
                  disabled={submitting}
                >
                  {BATCH_STATUSES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="edit-period-status"
                  className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500"
                >
                  Inquiry open / closed
                </label>
                <select
                  id="edit-period-status"
                  value={periodStatus}
                  onChange={(e) => setPeriodStatus(e.target.value)}
                  className={inputClass}
                  disabled={submitting}
                >
                  {PERIOD_STATUSES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error ? (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-medium text-rose-700">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => !submitting && onClose?.()}
                className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
                style={{ boxShadow: "none" }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-9 items-center rounded-md bg-[#007BFF] px-4 text-[13px] font-semibold text-white hover:bg-[#0069D9] disabled:opacity-60"
                style={{ border: "none", boxShadow: "none" }}
              >
                {submitting ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default EditInquiryBatchModal;
